import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  MessageCircle, ChevronLeft, Loader2, CalendarDays, ShieldCheck, Lock,
  Trash2, Flag, Pencil, MoreHorizontal, Ban, Check, X,
} from "lucide-react";

const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
const fmtTime = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const REPORT_LABELS = {
  harassment: "Harassment", hate: "Hate speech", explicit: "Explicit content",
  threat: "Threat or violence", spam: "Spam", personal_info: "Shares personal info", other: "Something else",
};

// Map common discussion error codes to friendly messages.
function boardError(err) {
  const s = err.response?.status;
  const detail = err.response?.data?.detail;
  if (s === 410) return "This discussion has closed and is no longer available.";
  if (s === 423) return "This discussion is locked — new comments are turned off.";
  if (s === 428) return detail || "Please set a community name and accept the notice first.";
  if (s === 429) return "You're doing that too fast. Please wait a moment and try again.";
  if (s === 409) return detail || "Looks like a duplicate — that was already posted.";
  return formatApiError(detail);
}

// --- participation gate (set community name + accept notice) ----------------
function ParticipationGate({ me, onReady }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const needName = !me.hasIdentity;
  const needAck = me.hasIdentity && !me.acknowledged;

  const saveName = async () => {
    if (name.trim().length < 2) return toast.error("Please choose a name of at least 2 characters.");
    setBusy(true);
    try { await api.post("/discussions/identity", { communityName: name.trim() }); toast.success("Community name set"); onReady(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const accept = async () => {
    setBusy(true);
    try { await api.post("/discussions/acknowledge", { policyVersion: me.policyVersion }); onReady(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!me.eligible) {
    return (
      <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-center" data-testid="board-ineligible">
        <ShieldCheck className="mx-auto mb-2 h-7 w-7 text-amber-600" />
        <p className="font-semibold text-amber-900">You can read every discussion</p>
        <p className="mt-1 text-sm text-amber-800/90">{me.eligibilityMessage || "Verify your email to join the conversation."}</p>
      </div>
    );
  }
  if (me.restricted) {
    return <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-center text-sm text-red-700" data-testid="board-restricted">Your account is currently restricted from posting on the board.</div>;
  }
  if (needName) {
    return (
      <div className="rounded-2xl border border-emerald-900/10 bg-white p-5" data-testid="board-set-name">
        <p className="font-semibold text-emerald-900">Choose a community name</p>
        <p className="mt-1 text-sm text-slate-500">This is shown next to your comments instead of your real name. 2–40 characters.</p>
        <div className="mt-3 flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder="e.g. Abu Yusuf" data-testid="board-name-input" />
          <Button onClick={saveName} disabled={busy} data-testid="board-name-save" className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        </div>
      </div>
    );
  }
  if (needAck) {
    return (
      <div className="rounded-2xl border border-emerald-900/10 bg-white p-5" data-testid="board-ack">
        <p className="text-sm text-slate-600">{me.ackText}</p>
        <Button onClick={accept} disabled={busy} data-testid="board-ack-accept" className="mt-3 rounded-full bg-emerald-800 text-white hover:bg-emerald-900">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "I understand — continue"}</Button>
      </div>
    );
  }
  return null;
}

// --- reactions --------------------------------------------------------------
function ReactionBar({ comment, onReact }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`reactions-${comment.id}`}>
      {(comment.reactions || []).map(({ key, emoji, count }) => {
        const active = comment.myReaction === key;
        return (
          <button key={key} onClick={() => onReact(comment, key)} title={key}
            data-testid={`react-${key}-${comment.id}`}
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 transition-colors ${active ? "bg-emerald-800 text-white ring-emerald-800" : "bg-white text-slate-600 ring-slate-200 hover:bg-mint hover:text-emerald-900"}`}>
            <span className="text-sm leading-none">{emoji}</span>{count > 0 && <span>{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

function CommentActions({ c, reasons, onEdit, onDelete, onReport, onBlock }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button aria-label="Comment actions" data-testid={`comment-menu-${c.id}`} className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><MoreHorizontal className="h-4 w-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {c.mine && <DropdownMenuItem onClick={() => onEdit(c)} data-testid={`edit-${c.id}`}><Pencil className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>}
        {c.mine && <DropdownMenuItem onClick={() => onDelete(c)} data-testid={`delete-${c.id}`} className="text-red-600"><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>}
        {!c.mine && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid={`report-${c.id}`}><Flag className="mr-2 h-4 w-4" /> Report</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {(reasons || ["other"]).map((r) => (
                <DropdownMenuItem key={r} onClick={() => onReport(c, r)} data-testid={`report-${r}-${c.id}`}>{REPORT_LABELS[r] || r}</DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        {!c.mine && <DropdownMenuSeparator />}
        {!c.mine && <DropdownMenuItem onClick={() => onBlock(c)} data-testid={`block-${c.id}`} className="text-red-600"><Ban className="mr-2 h-4 w-4" /> Block author</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CommentBody({ c, reasons, canReply, onReact, onEdit, onDelete, onReport, onBlock, onReply, editing, onSaveEdit, onCancelEdit, commentMax }) {
  const [replying, setReplying] = useState(false);
  const [rtext, setRtext] = useState("");
  const [etext, setEtext] = useState(c.text);
  return (
    <div data-testid={`comment-${c.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-emerald-900">{c.author?.communityName || "Member"}{c.edited && <span className="ml-2 text-xs font-normal text-slate-400">(edited)</span>}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">{fmtTime(c.createdAt)}</span>
          {!c.deleted && <CommentActions c={c} reasons={reasons} onEdit={onEdit} onDelete={onDelete} onReport={onReport} onBlock={onBlock} />}
        </div>
      </div>
      {editing ? (
        <div className="mt-2">
          <Textarea value={etext} maxLength={commentMax} onChange={(e) => setEtext(e.target.value)} data-testid={`edit-input-${c.id}`} className="text-sm" />
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={() => onSaveEdit(c, etext)} data-testid={`edit-save-${c.id}`} className="rounded-full bg-emerald-800 text-white"><Check className="mr-1 h-3.5 w-3.5" /> Save</Button>
            <Button size="sm" variant="ghost" onClick={onCancelEdit}><X className="mr-1 h-3.5 w-3.5" /> Cancel</Button>
          </div>
        </div>
      ) : (
        <p className={`mt-1.5 whitespace-pre-wrap ${c.deleted ? "italic text-slate-400" : "text-slate-700"}`}>{c.deleted ? "Comment deleted" : c.text}</p>
      )}
      {!c.deleted && !editing && (
        <div className="flex items-center justify-between">
          <ReactionBar comment={c} onReact={onReact} />
          {canReply && <button onClick={() => setReplying((v) => !v)} data-testid={`reply-btn-${c.id}`} className="mt-2 text-xs font-medium text-emerald-700 hover:underline">Reply</button>}
        </div>
      )}
      {replying && (
        <div className="mt-3">
          <Textarea value={rtext} maxLength={commentMax} onChange={(e) => setRtext(e.target.value)} placeholder="Write a respectful reply…" data-testid={`reply-input-${c.id}`} className="text-sm" />
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={async () => { const ok = await onReply(c.id, rtext); if (ok) { setRtext(""); setReplying(false); } }} data-testid={`reply-submit-${c.id}`} className="rounded-full bg-emerald-800 text-white">Reply</Button>
            <Button size="sm" variant="ghost" onClick={() => setReplying(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Discussion({ topicId, onBack, config, me, refreshMe }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [comments, setComments] = useState(null);
  const [sort, setSort] = useState("new");
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [gone, setGone] = useState(false);
  const canParticipate = !!user && me && me.hasIdentity && me.acknowledged && me.eligible && !me.restricted;
  const locked = topic?.locked;

  const loadTopic = useCallback(async () => {
    try { const { data } = await api.get(`/discussions/topic/${topicId}`); setTopic(data); }
    catch (err) { if (err.response?.status === 410) setGone(true); else toast.error(boardError(err)); }
  }, [topicId]);
  const loadComments = useCallback(async () => {
    try { const { data } = await api.get(`/discussions/topic/${topicId}/comments`, { params: { sort, limit: 100 } }); setComments(data.items || []); }
    catch (err) { if (err.response?.status === 410) setGone(true); else setComments([]); }
  }, [topicId, sort]);

  useEffect(() => { loadTopic(); }, [loadTopic]);
  useEffect(() => { loadComments(); }, [loadComments]);

  const post = async (body, parentId) => {
    if (!body.trim()) return false;
    try {
      const { data } = await api.post(`/discussions/topic/${topicId}/comments`, { text: body.trim(), parentId: parentId || undefined });
      if (data.status === "approved") { toast.success("Comment posted"); await loadComments(); await loadTopic(); return true; }
      toast.info(data.message || "Your comment was sent for review and will appear once approved.");
      return true;
    } catch (err) { toast.error(boardError(err)); return false; }
  };
  const submit = async () => { setPosting(true); const ok = await post(text); if (ok) setText(""); setPosting(false); };

  const react = async (c, key) => {
    if (!user) return navigate(`/login?next=/marriage-board?t=${topicId}`);
    if (!canParticipate) { toast.error("Set a community name and accept the notice to react."); return; }
    try { await api.post(`/discussions/comment/${c.id}/react`, { key }); await loadComments(); }
    catch (err) { toast.error(boardError(err)); }
  };
  const del = async (c) => { try { await api.delete(`/discussions/comment/${c.id}`); await loadComments(); await loadTopic(); toast.success("Comment deleted"); } catch (err) { toast.error(boardError(err)); } };
  const saveEdit = async (c, newText) => {
    try { const { data } = await api.patch(`/discussions/comment/${c.id}`, { text: newText.trim() }); setEditingId(null);
      if (data.status && data.status !== "approved") toast.info("Edited comment sent for review."); else toast.success("Comment updated");
      await loadComments();
    } catch (err) { toast.error(boardError(err)); }
  };
  const report = async (c, reason) => { try { await api.post(`/discussions/comment/${c.id}/report`, { reason }); toast.success("Thank you. Our team will review this."); } catch (err) { toast.error(boardError(err)); } };
  const block = async (c) => { try { await api.post("/discussions/block", { commentId: c.id }); toast.success("You won't see this person's comments anymore."); await loadComments(); } catch (err) { toast.error(boardError(err)); } };

  if (gone) return (
    <div className="rounded-3xl border border-dashed border-emerald-300 bg-white py-16 text-center" data-testid="board-gone">
      <CalendarDays className="mx-auto mb-3 h-8 w-8 text-slate-400" />
      <p className="text-slate-600">This discussion has closed and is no longer available.</p>
      <Button onClick={onBack} className="mt-4 rounded-full bg-emerald-800 text-white">Back to discussions</Button>
    </div>
  );
  if (!topic) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>;

  const renderNode = (c) => (
    <div key={c.id} className="rounded-2xl border border-emerald-900/10 bg-white p-4">
      <CommentBody c={c} reasons={config?.reportReasons} canReply={canParticipate && !locked} onReact={react} onEdit={(x) => setEditingId(x.id)} onDelete={del}
        onReport={report} onBlock={block} onReply={post} editing={editingId === c.id}
        onSaveEdit={saveEdit} onCancelEdit={() => setEditingId(null)} commentMax={config?.commentMax || 1000} />
      {c.replies?.length > 0 && (
        <div className="mt-3 space-y-3 border-l-2 border-emerald-100 pl-4">
          {c.replies.map((r) => (
            <div key={r.id} className="rounded-xl bg-mint/30 p-3">
              <CommentBody c={r} reasons={config?.reportReasons} canReply={false} onReact={react} onEdit={(x) => setEditingId(x.id)} onDelete={del}
                onReport={report} onBlock={block} onReply={post} editing={editingId === r.id}
                onSaveEdit={saveEdit} onCancelEdit={() => setEditingId(null)} commentMax={config?.commentMax || 1000} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="board-discussion">
      <button onClick={onBack} data-testid="board-back" className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline"><ChevronLeft className="h-4 w-4" /> All discussions</button>
      <div className="rounded-3xl border border-emerald-900/10 bg-gradient-to-br from-emerald-800 to-emerald-950 p-6 text-white sm:p-8">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-amber-200"><CalendarDays className="h-3.5 w-3.5" /> {topic.category} · {fmtDate(topic.boardDate)}</span>
        <h1 className="mt-3 font-serif text-2xl font-bold sm:text-3xl">{topic.question}</h1>
        {topic.intro && <p className="mt-2 text-emerald-100/90">{topic.intro}</p>}
        {locked && <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-black/20 px-3 py-1 text-xs font-medium text-amber-100"><Lock className="h-3.5 w-3.5" /> Comments are closed</p>}
      </div>

      <div className="mt-6">
        {!user ? (
          <div className="rounded-2xl border border-dashed border-emerald-300 bg-mint/40 p-5 text-center" data-testid="board-signin-prompt">
            <p className="text-emerald-900">Sign in with your verified account to join the discussion.</p>
            <Link to={`/login?next=/marriage-board?t=${topicId}`}><Button className="mt-3 rounded-full bg-emerald-800 text-white" data-testid="board-signin-btn">Sign in to comment</Button></Link>
          </div>
        ) : !canParticipate ? (
          <ParticipationGate me={me} onReady={refreshMe} />
        ) : locked ? null : (
          <div className="rounded-2xl border border-emerald-900/10 bg-white p-4">
            <Textarea value={text} maxLength={config?.commentMax || 1000} onChange={(e) => setText(e.target.value)} placeholder="Share a respectful, thoughtful comment…" data-testid="comment-input" />
            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs text-slate-400">{text.length}/{config?.commentMax || 1000}</span>
              <Button onClick={submit} disabled={posting || !text.trim()} data-testid="comment-submit" className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900">{posting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post comment"}</Button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">{topic.commentCount} {topic.commentCount === 1 ? "comment" : "comments"}</h2>
          <div className="flex gap-1.5">
            {[["new", "Newest"], ["top", "Top"], ["oldest", "Oldest"]].map(([k, l]) => (
              <button key={k} onClick={() => setSort(k)} data-testid={`sort-${k}`} className={`rounded-full px-3 py-1 text-xs font-medium ${sort === k ? "bg-emerald-800 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-mint"}`}>{l}</button>
            ))}
          </div>
        </div>
        {comments === null ? (
          <div className="space-y-3">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
        ) : comments.length === 0 ? (
          <p className="text-slate-500">Be the first to share your thoughts.</p>
        ) : (
          <div className="space-y-3">{comments.map(renderNode)}</div>
        )}
      </div>
    </div>
  );
}

export default function MarriageBoard() {
  const { user } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [openId, setOpenId] = useState(params.get("t") || null);
  const [config, setConfig] = useState(null);
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState("");
  const [feed, setFeed] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [me, setMe] = useState(null);
  const [isStaff, setIsStaff] = useState(false);

  const refreshMe = useCallback(async () => {
    if (!user) { setMe(null); return; }
    try { const { data } = await api.get("/discussions/me"); setMe(data); } catch { setMe(null); }
  }, [user]);

  useEffect(() => {
    api.get("/discussions/config").then(({ data }) => setConfig(data)).catch(() => {});
    api.get("/discussions/categories").then(({ data }) => setCats(data.categories || [])).catch(() => {});
  }, []);
  useEffect(() => { refreshMe(); }, [refreshMe]);
  useEffect(() => {
    if (!user) { setIsStaff(false); return; }
    api.get("/discussions/admin/status").then(() => setIsStaff(true)).catch(() => setIsStaff(false));
  }, [user]);

  const loadFeed = useCallback(async (cursor) => {
    try {
      const { data } = await api.get("/discussions/feed", { params: { category: category || undefined, cursor: cursor || undefined, limit: 15 } });
      setHasMore(!!data.hasMore); setNextCursor(data.nextCursor || null);
      setFeed((prev) => (cursor && prev ? [...prev, ...(data.items || [])] : (data.items || [])));
    } catch { setFeed([]); }
  }, [category]);
  useEffect(() => { if (!openId) { setFeed(null); loadFeed(); } }, [loadFeed, openId]);

  const loadMore = async () => { setLoadingMore(true); await loadFeed(nextCursor); setLoadingMore(false); };
  const open = (id) => { setOpenId(id); window.history.replaceState({}, "", `/marriage-board?t=${id}`); };
  const close = () => { setOpenId(null); window.history.replaceState({}, "", "/marriage-board"); refreshMe(); };

  const catName = useMemo(() => Object.fromEntries(cats.map((c) => [c.code, c.name])), [cats]);

  return (
    <div className="min-h-screen bg-ivory">
      <header className="sticky top-0 z-20 border-b border-emerald-900/10 bg-ivory/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link to="/" data-testid="board-home-link"><Logo className="h-9" /></Link>
          <div className="flex items-center gap-2">
            {isStaff && <Link to="/marriage-board/admin"><Button variant="outline" data-testid="board-admin-link" className="rounded-full border-amber-500/50 text-amber-700 hover:bg-amber-50"><ShieldCheck className="mr-1.5 h-4 w-4" /> Admin</Button></Link>}
            {user ? (
              <Link to="/discovery"><Button variant="outline" className="rounded-full border-emerald-800/30 text-emerald-900">My account</Button></Link>
            ) : (
              <Link to="/login"><Button className="rounded-full bg-emerald-800 text-white" data-testid="board-header-login">Sign in</Button></Link>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8">
        {openId ? (
          <Discussion topicId={openId} onBack={close} config={config} me={me} refreshMe={refreshMe} />
        ) : (
          <>
            <div className="mb-6 text-center">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-mint px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-800"><MessageCircle className="h-3.5 w-3.5" /> Community</span>
              <h1 className="mt-3 font-serif text-3xl font-bold text-emerald-950 sm:text-4xl">{config?.title || "Marriage Discussions"}</h1>
              <p className="mt-2 text-slate-600">{config?.subtitle || "Share your thoughts. Learn what matters to others."}</p>
              <p className="mt-1 text-xs font-medium text-emerald-700">New topic daily · scroll for the past month{config?.timezone ? ` · ${config.timezone} time` : ""}</p>
            </div>

            {/* Category filter */}
            {cats.length > 0 && (
              <div className="mb-6 flex gap-2 overflow-x-auto pb-1" data-testid="board-categories">
                <button onClick={() => setCategory("")} data-testid="cat-all" className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium ${category === "" ? "bg-emerald-800 text-white" : "bg-white text-emerald-900/70 ring-1 ring-emerald-900/10 hover:bg-mint"}`}>All</button>
                {cats.map((c) => (
                  <button key={c.code} onClick={() => setCategory(c.code)} data-testid={`cat-${c.code}`} className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium ${category === c.code ? "bg-emerald-800 text-white" : "bg-white text-emerald-900/70 ring-1 ring-emerald-900/10 hover:bg-mint"}`}>{c.name}</button>
                ))}
              </div>
            )}

            {feed === null ? (
              <div className="space-y-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-3xl" />)}</div>
            ) : feed.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-emerald-300 bg-white py-16 text-center text-slate-500" data-testid="board-empty">No discussions in this category yet. Please check back soon, inshaAllah.</div>
            ) : (
              <div className="space-y-4" data-testid="board-feed">
                {feed.map((d) => (
                  <button key={d.id} onClick={() => open(d.id)} data-testid={`feed-item-${d.boardDate}`}
                    className="block w-full rounded-3xl border border-emerald-900/10 bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md sm:p-6">
                    <span className="text-xs font-semibold uppercase tracking-wide text-amber-600">{d.category || catName[d.categoryCode] || "General"} · {fmtDate(d.boardDate)}</span>
                    <h3 className="mt-1.5 font-serif text-xl font-bold text-emerald-950">{d.question}</h3>
                    {d.intro && <p className="mt-1.5 line-clamp-2 text-slate-600">{d.intro}</p>}
                    <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700">
                      {d.locked && <Lock className="h-3.5 w-3.5 text-slate-400" />}
                      <MessageCircle className="h-4 w-4" /> {d.commentCount} {d.commentCount === 1 ? "comment" : "comments"}
                    </span>
                  </button>
                ))}
                {hasMore && (
                  <div className="text-center">
                    <Button onClick={loadMore} disabled={loadingMore} variant="outline" data-testid="board-load-more" className="rounded-full border-emerald-800/30 text-emerald-900">{loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load earlier discussions"}</Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
